import React, { useState, useCallback, useMemo } from 'react'
import {
	Button,
	TextField,
	Box,
	Typography,
	InputAdornment,
	IconButton,
	Alert,
} from '@mui/material'
import { useNavigate, Link } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import {
	alertModal,
	autoCellPhone,
	timerAlert,
	validEmail,
} from '../../util/util'
import {
	LoginContainer,
	GridBackground,
	LoginPaper,
} from 'components/ApplicationStyles'
import { userSchema } from '../../schemas/userSchema'
import { z } from 'zod'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import LockIcon from '@mui/icons-material/Lock'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import PhoneIcon from '@mui/icons-material/Phone'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import axios from 'axios'

// 타입 정의 (userSchema에서 추론)
type JoinRequest = z.infer<typeof userSchema>

interface ValidationState {
	usernameChecked: boolean
	usernameAvailable: boolean
	emailValid: boolean
	emailChecked: boolean
	emailAvailable: boolean
	passwordMatch: boolean
}

interface FormErrors {
	[key: string]: string
}

// 에러 처리 유틸리티
const isZodError = (error: unknown): error is z.ZodError => {
	return error instanceof z.ZodError
}

const getErrorMessage = (error: unknown): string => {
	if (isZodError(error)) {
		return error.issues[0]?.message || '유효하지 않은 값입니다.'
	}
	if (error instanceof Error) {
		return error.message
	}
	return '알 수 없는 오류가 발생했습니다.'
}

// 스타일링된 컴포넌트
const TechTextField = styled(TextField)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		background: 'rgba(255, 255, 255, 0.05)',
		borderRadius: '12px',
		color: 'white',
		transition: 'all 0.3s ease',
		'& fieldset': {
			borderColor: 'rgba(255, 255, 255, 0.2)',
			borderWidth: '1px',
		},
		'&:hover fieldset': {
			borderColor: '#4C51D3',
			boxShadow: '0 0 10px rgba(76, 81, 211, 0.3)',
		},
		'&.Mui-focused fieldset': {
			borderColor: '#4C51D3',
			borderWidth: '2px',
			boxShadow: '0 0 15px rgba(76, 81, 211, 0.5)',
		},
	},
	'& .MuiInputLabel-root': {
		color: 'rgba(255, 255, 255, 0.7)',
		'&.Mui-focused': {
			color: '#4C51D3',
		},
	},
	'& .MuiInputBase-input': {
		color: 'white',
	},
	'& .MuiInputAdornment-root': {
		color: 'rgba(255, 255, 255, 0.7)',
	},
}))

const TechTypography = styled(Typography)(({ theme }) => ({
	background: 'linear-gradient(45deg, #4C51D3, #7184AD)',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	fontWeight: 700,
	textAlign: 'center',
	marginBottom: theme.spacing(3),
}))

const DuplicateCheckButton = styled(Button)(({ theme }) => ({
	minWidth: '100px',
	backgroundColor: 'rgba(76, 81, 211, 0.8)',
	color: 'white',
	borderRadius: '12px',
	'&:hover': {
		backgroundColor: 'rgba(76, 81, 211, 1)',
	},
	'&:disabled': {
		backgroundColor: 'rgba(76, 81, 211, 0.3)',
		color: 'rgba(255, 255, 255, 0.3)',
	},
}))

const API_ENDPOINTS = {
	checkUsername: `${process.env.REACT_APP_HOST}/auth/existsByUsername`,
	checkEmail: `${process.env.REACT_APP_HOST}/auth/existsByEmail`,
	register: `${process.env.REACT_APP_HOST}/auth/register`,
}

const Join = () => {
	const navigate = useNavigate()

	// State 관리
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [joinRequest, setJoinRequest] = useState<JoinRequest>({
		username: '',
		email: '',
		password: '',
		passwordConfirm: '',
		name: '',
		phone: '',
		id: undefined,
	})
	const [validation, setValidation] = useState<ValidationState>({
		usernameChecked: false,
		usernameAvailable: false,
		emailValid: false,
		emailChecked: false,
		emailAvailable: false,
		passwordMatch: false,
	})
	const [formErrors, setFormErrors] = useState<FormErrors>({})
	const [errorMessage, setErrorMessage] = useState('')
	const [isError, setIsError] = useState(false)
	const [submitDisabled, setSubmitDisabled] = useState(false)

	// 에러 상태 초기화
	const clearError = useCallback(() => {
		setIsError(false)
		setErrorMessage('')
	}, [])

	// 개별 필드 스키마 검증
	const validateField = useCallback((fieldName: string, value: any) => {
		try {
			const fieldSchema =
				userSchema.shape[fieldName as keyof typeof userSchema.shape]
			if (fieldSchema) {
				fieldSchema.parse(value)
				setFormErrors((prev) => ({ ...prev, [fieldName]: '' }))
				return true
			}
		} catch (error) {
			const err = error as unknown
			if (isZodError(err)) {
				setFormErrors((prev) => ({
					...prev,
					[fieldName]: err.issues[0]?.message || '유효하지 않은 값입니다.',
				}))
			}
			return false
		}
		return true
	}, [])

	// Input 변경 핸들러
	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = e.target
			clearError()

			let processedValue = value

			// 전화번호 자동 하이픈 처리
			if (name === 'phone') {
				processedValue = autoCellPhone(value) || value
			} else {
				processedValue = value.trim()
			}

			// State 업데이트
			setJoinRequest((prev) => ({
				...prev,
				[name]: processedValue,
			}))

			// 필드별 검증 및 상태 업데이트
			if (name === 'username') {
				validateField(name, processedValue)
				setValidation((prev) => ({
					...prev,
					usernameChecked: false,
					usernameAvailable: false,
				}))
			} else if (name === 'email') {
				const isValidEmail = validEmail(processedValue)
				validateField(name, processedValue)
				setValidation((prev) => ({
					...prev,
					emailValid: isValidEmail,
					emailChecked: false,
					emailAvailable: false,
				}))
			} else if (name === 'phone') {
				validateField(name, processedValue)
			} else if (name === 'name') {
				validateField(name, processedValue)
			} else if (name === 'password' || name === 'passwordConfirm') {
				const password = name === 'password' ? processedValue : joinRequest.password
				const confirmPassword =
					name === 'passwordConfirm' ? processedValue : joinRequest.passwordConfirm

				validateField(name, processedValue)

				setValidation((prev) => ({
					...prev,
					passwordMatch: password === confirmPassword && password.length > 0,
				}))
			}
		},
		[clearError, joinRequest.password, joinRequest.passwordConfirm, validateField]
	)

	// 비밀번호 표시 토글
	const togglePasswordVisibility = useCallback(() => {
		setShowPassword((prev) => !prev)
	}, [])

	const toggleConfirmPasswordVisibility = useCallback(() => {
		setShowConfirmPassword((prev) => !prev)
	}, [])

	// 아이디 중복확인
	const checkUsernameDuplicate = useCallback(async () => {
		if (!joinRequest.username) {
			alertModal('아이디를 입력해주세요.', 'warning', () => {})
			return
		}

		try {
			// 아이디 중복 API 호출
			const response = await axios.get(
				`${API_ENDPOINTS.checkUsername}/${joinRequest.username}`
			)

			if (!response.data.success) {
				alertModal(response.data.data, 'error', () => {})

				setValidation((prev) => ({
					...prev,
					usernameChecked: true,
					usernameAvailable: false,
				}))
				return
			}

			setValidation((prev) => ({
				...prev,
				usernameChecked: true,
				usernameAvailable: true,
			}))

			console.log(response.data)
			timerAlert(response.data.data, 'success', () => {})
		} catch (error) {
			const err = error as unknown
			console.error('아이디 중복확인 오류:', getErrorMessage(err))
			alertModal('중복확인 중 오류가 발생했습니다.', 'error', () => {})
		}
	}, [joinRequest.username])

	// 이메일 중복확인
	const checkEmailDuplicate = useCallback(async () => {
		if (!joinRequest.email) {
			alertModal('이메일을 입력해주세요.', 'warning', () => {})
			return
		}

		if (!validation.emailValid) {
			alertModal('유효한 이메일 주소를 입력해주세요.', 'warning', () => {})
			return
		}

		try {
			// 이메일 중복 API 호출
			const response = await axios.get(
				`${API_ENDPOINTS.checkEmail}/${joinRequest.email}`
			)

			if (!response.data.success) {
				alertModal(response.data.data, 'error', () => {})

				setValidation((prev) => ({
					...prev,
					emailChecked: true,
					emailAvailable: false,
				}))

				return
			}

			setValidation((prev) => ({
				...prev,
				emailChecked: true,
				emailAvailable: true,
			}))

			timerAlert(response.data.data, 'success', () => {})
		} catch (error) {
			const err = error as unknown
			console.error('이메일 중복확인 오류:', getErrorMessage(err))
			alertModal('중복확인 중 오류가 발생했습니다.', 'error', () => {})
		}
	}, [joinRequest.email, validation.emailValid])

	// 회원가입 처리
	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()

			console.log('회원가입 요청 데이터:', joinRequest)

			// 스키마 전체 검증
			try {
				userSchema.parse(joinRequest)
			} catch (error) {
				const err = error as unknown
				if (isZodError(err)) {
					const errorMessages = err.issues.map((issue) => issue.message).join('\n')
					setErrorMessage(errorMessages)
					setIsError(true)
					return
				}
			}

			// 필수 필드 검사
			if (
				!joinRequest.username ||
				!joinRequest.email ||
				!joinRequest.password ||
				!joinRequest.passwordConfirm ||
				!joinRequest.name ||
				!joinRequest.phone
			) {
				setErrorMessage('모든 필드를 입력해주세요.')
				setIsError(true)
				return
			}

			if (!validation.usernameChecked || !validation.usernameAvailable) {
				setErrorMessage('아이디 중복확인을 완료해주세요.')
				setIsError(true)
				return
			}

			if (!validation.emailChecked || !validation.emailAvailable) {
				setErrorMessage('이메일 중복확인을 완료해주세요.')
				setIsError(true)
				return
			}

			if (!validation.passwordMatch) {
				setErrorMessage('비밀번호가 일치하지 않습니다.')
				setIsError(true)
				return
			}

			if (joinRequest.password.length < 6) {
				setErrorMessage('비밀번호는 6자 이상이어야 합니다.')
				setIsError(true)
				return
			}

			setSubmitDisabled(true)
			clearError()

			try {
				const response = await axios.post(API_ENDPOINTS.register, {
					username: joinRequest.username,
					email: joinRequest.email,
					password: joinRequest.password,
					name: joinRequest.name,
					phone: joinRequest.phone,
				})

				console.log(response)

				if (response.data.success && response.data.data) {
					alertModal(response.data.data, 'success', () => {
						navigate('/')
					})
				} else {
					alertModal(
						'회원가입에 실패했습니다. 문제가 지속될 경우 관리자에게 문의하세요.',
						'error',
						() => {}
					)
				}
			} catch (error) {
				const err = error as unknown
				console.error('회원가입 오류:', getErrorMessage(err))
				setErrorMessage('회원가입 중 오류가 발생했습니다. 다시 시도해주세요.')
				setIsError(true)
			} finally {
				setSubmitDisabled(false)
			}
		},
		[joinRequest, validation, navigate, clearError]
	)

	// 스타일 색상 상수
	const iconColor = useMemo(() => ({ color: 'rgba(255, 255, 255, 0.7)' }), [])
	const errorAlertStyles = useMemo(
		() => ({
			mb: 2,
			background: 'rgba(244, 67, 54, 0.1)',
			border: '1px solid rgba(244, 67, 54, 0.3)',
			color: '#ff6b6b',
			'& .MuiAlert-icon': {
				color: '#ff6b6b',
			},
		}),
		[]
	)

	return (
		<LoginContainer
			maxWidth={false}
			disableGutters
		>
			<GridBackground />
			<LoginPaper elevation={0}>
				<Box
					component="form"
					onSubmit={handleSubmit}
					sx={{ width: '100%' }}
				>
					{/* 로고 및 제목 */}
					<TechTypography variant="h4">Senior Health Care</TechTypography>
					<Typography
						variant="body2"
						sx={{
							color: 'rgba(255, 255, 255, 0.7)',
							textAlign: 'center',
							mb: 4,
							fontSize: '14px',
						}}
					>
						회원가입
					</Typography>

					{/* 에러 메시지 */}
					{isError && (
						<Alert
							severity="error"
							sx={errorAlertStyles}
						>
							{errorMessage}
						</Alert>
					)}

					{/* 아이디 입력 */}
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<TechTextField
								fullWidth
								id="username"
								name="username"
								label="아이디"
								type="text"
								autoComplete="username"
								onChange={handleInputChange}
								error={Boolean(formErrors.username)}
								helperText={formErrors.username}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<PersonIcon sx={iconColor} />
										</InputAdornment>
									),
									endAdornment: validation.usernameChecked &&
										validation.usernameAvailable && (
											<InputAdornment position="end">
												<CheckCircleIcon sx={{ color: '#4CAF50' }} />
											</InputAdornment>
										),
								}}
								sx={{
									'& .MuiFormHelperText-root': {
										color: '#ff6b6b',
									},
								}}
							/>
							<DuplicateCheckButton
								variant="contained"
								onClick={checkUsernameDuplicate}
								disabled={!joinRequest.username}
							>
								중복확인
							</DuplicateCheckButton>
						</Box>
					</Box>

					{/* 이메일 입력 */}
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<TechTextField
								fullWidth
								id="email"
								name="email"
								label="이메일"
								type="email"
								autoComplete="email"
								onChange={handleInputChange}
								error={Boolean(formErrors.email)}
								helperText={formErrors.email}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<EmailIcon sx={iconColor} />
										</InputAdornment>
									),
									endAdornment: validation.emailChecked && validation.emailAvailable && (
										<InputAdornment position="end">
											<CheckCircleIcon sx={{ color: '#4CAF50' }} />
										</InputAdornment>
									),
								}}
								sx={{
									'& .MuiFormHelperText-root': {
										color: '#ff6b6b',
									},
								}}
							/>
							<DuplicateCheckButton
								variant="contained"
								onClick={checkEmailDuplicate}
								disabled={!joinRequest.email || !validation.emailValid}
							>
								중복확인
							</DuplicateCheckButton>
						</Box>
					</Box>

					{/* 전화번호 입력 */}
					<Box sx={{ mb: 2 }}>
						<TechTextField
							fullWidth
							id="phone"
							name="phone"
							label="전화번호"
							type="text"
							autoComplete="tel"
							onChange={handleInputChange}
							value={joinRequest.phone}
							inputProps={{ maxLength: 13 }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PhoneIcon sx={iconColor} />
									</InputAdornment>
								),
							}}
							placeholder="010-1234-5678"
							error={Boolean(formErrors.phone)}
							helperText={formErrors.phone}
							sx={{
								'& .MuiFormHelperText-root': {
									color: '#ff6b6b',
								},
							}}
						/>
					</Box>

					{/* 비밀번호 입력 */}
					<Box sx={{ mb: 2 }}>
						<TechTextField
							fullWidth
							id="password"
							name="password"
							label="비밀번호"
							type={showPassword ? 'text' : 'password'}
							autoComplete="new-password"
							onChange={handleInputChange}
							error={Boolean(formErrors.password)}
							helperText={formErrors.password}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockIcon sx={iconColor} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={togglePasswordVisibility}
											edge="end"
											sx={iconColor}
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
							sx={{
								'& .MuiFormHelperText-root': {
									color: '#ff6b6b',
								},
							}}
						/>
					</Box>

					{/* 비밀번호 확인 입력 */}
					<Box sx={{ mb: 2 }}>
						<TechTextField
							fullWidth
							id="passwordConfirm"
							name="passwordConfirm"
							label="비밀번호 확인"
							type={showConfirmPassword ? 'text' : 'password'}
							autoComplete="new-password"
							onChange={handleInputChange}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockIcon sx={iconColor} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											{validation.passwordMatch && joinRequest.passwordConfirm && (
												<CheckCircleIcon sx={{ color: '#4CAF50' }} />
											)}
											<IconButton
												aria-label="toggle confirm password visibility"
												onClick={toggleConfirmPasswordVisibility}
												edge="end"
												sx={iconColor}
											>
												{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</Box>
									</InputAdornment>
								),
							}}
							error={Boolean(
								formErrors.passwordConfirm ||
									(joinRequest.passwordConfirm && !validation.passwordMatch)
							)}
							helperText={
								formErrors.passwordConfirm ||
								(joinRequest.passwordConfirm && !validation.passwordMatch
									? '비밀번호가 일치하지 않습니다'
									: '')
							}
							sx={{
								'& .MuiFormHelperText-root': {
									color: '#ff6b6b',
								},
							}}
						/>
					</Box>

					{/* 이름 입력 */}
					<Box sx={{ mb: 3 }}>
						<TechTextField
							fullWidth
							id="name"
							name="name"
							label="이름"
							type="text"
							autoComplete="name"
							onChange={handleInputChange}
							error={Boolean(formErrors.name)}
							helperText={formErrors.name}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<AccountCircleIcon sx={iconColor} />
									</InputAdornment>
								),
							}}
							sx={{
								'& .MuiFormHelperText-root': {
									color: '#ff6b6b',
								},
							}}
						/>
					</Box>

					{/* 회원가입 버튼 */}
					<Button
						type="submit"
						fullWidth
						variant="contained"
						disabled={submitDisabled}
						sx={{ mb: 2 }}
					>
						{submitDisabled ? '회원가입 중...' : '회원가입'}
					</Button>

					{/* 로그인 링크 */}
					<Box sx={{ textAlign: 'center', mt: 2 }}>
						<Typography
							variant="body2"
							sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
						>
							이미 계정이 있으신가요?{' '}
							<Link
								to="/"
								style={{
									color: '#4C51D3',
									textDecoration: 'none',
									fontWeight: 'bold',
								}}
							>
								로그인
							</Link>
						</Typography>
					</Box>

					{/* 추가 정보 */}
					<Typography
						variant="caption"
						sx={{
							color: 'rgba(255, 255, 255, 0.5)',
							textAlign: 'center',
							display: 'block',
							mt: 2,
						}}
					>
						© 2025 Senior Health Care. All rights reserved.
					</Typography>
				</Box>
			</LoginPaper>
		</LoginContainer>
	)
}

export default Join
